import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function formatRelativeTime(date: string | Date): string {
	const now = new Date();
	const d = typeof date === "string" ? new Date(date) : date;
	const diffMs = now.getTime() - d.getTime();
	const seconds = Math.floor(diffMs / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);

	if (seconds < 60) return "just now";
	if (minutes < 60) return `${minutes}m ago`;
	if (hours < 24) return `${hours}h ago`;
	if (days < 7) return `${days}d ago`;
	if (days < 30) return `${Math.floor(days / 7)}w ago`;
	return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function formatNumber(n: number): string {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
	return n.toLocaleString();
}

export function formatPercent(n: number): string {
	return `${Math.round(n * 100)}%`;
}

export function formatCurrency(n: number): string {
	return `$${n.toFixed(2)}`;
}

export function truncate(str: string, length: number): string {
	if (str.length <= length) return str;
	return `${str.slice(0, length).trimEnd()}\u2026`;
}

export function getGreeting(): string {
	const hour = new Date().getHours();
	if (hour < 12) return "Good morning";
	if (hour < 17) return "Good afternoon";
	return "Good evening";
}

export function getCategoryColor(category: string): string {
	const map: Record<string, string> = {
		article: "text-blue-400 bg-blue-400/10 border-blue-400/20",
		pdf: "text-orange-400 bg-orange-400/10 border-orange-400/20",
		video: "text-purple-400 bg-purple-400/10 border-purple-400/20",
		podcast: "text-pink-400 bg-pink-400/10 border-pink-400/20",
		email: "text-cyan-400 bg-cyan-400/10 border-cyan-400/20",
		rss: "text-amber-400 bg-amber-400/10 border-amber-400/20",
		tweet: "text-sky-400 bg-sky-400/10 border-sky-400/20",
		highlight: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
	};
	return map[category] ?? "text-muted-foreground bg-muted/50 border-border";
}

export function getStatusColor(status: string): string {
	const map: Record<string, string> = {
		queued: "text-muted-foreground bg-muted/50",
		proposing: "text-warning bg-warning/10",
		awaiting_approval: "text-info bg-info/10",
		committed: "text-success bg-success/10",
		skipped: "text-muted-foreground bg-muted/30",
		error: "text-destructive bg-destructive/10",
	};
	return map[status] ?? "text-muted-foreground bg-muted/50";
}

export function getProcessingStatusColor(status: string): string {
	const map: Record<string, string> = {
		queued: "text-muted-foreground bg-muted/50",
		processing: "text-info bg-info/10",
		completed: "text-success bg-success/10",
		failed: "text-destructive bg-destructive/10",
	};
	return map[status] ?? "text-muted-foreground bg-muted/50";
}
